import React, { useState, useEffect, useCallback } from 'react';
import {
    Search, ChevronLeft, ChevronRight, Loader2, AlertTriangle,
    DatabaseZap, Filter, User, X, ShieldAlert,
} from 'lucide-react';
import { adminAnalytics } from '../../services/api';
import EventPayloadDrawer from './EventPayloadDrawer';
import UserTimelineDrawer from './UserTimelineDrawer';

/**
 * EventExplorerTabV2 — Enhanced event explorer with extra filters
 * (request_id, feature_area, deployment_version) and user timeline drill-down.
 */

const EventExplorerTabV2 = ({ filters = {} }) => {
    const [events, setEvents] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(50);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedEvent, setSelectedEvent] = useState(null);

    // User timeline drawer
    const [timelineUser, setTimelineUser] = useState(null);

    // Local filters
    const [eventCategory, setEventCategory] = useState('');
    const [eventName, setEventName] = useState('');
    const [userUuid, setUserUuid] = useState('');
    const [sessionId, setSessionId] = useState('');
    const [routeFilter, setRouteFilter] = useState('');
    const [requestId, setRequestId] = useState('');
    const [featureAreaLocal, setFeatureAreaLocal] = useState('');
    const [deployVersionLocal, setDeployVersionLocal] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = {
                days: filters.days || 7,
                page,
                page_size: pageSize,
                ...(filters.district_id && { district_id: filters.district_id }),
                ...(filters.campus_id && { campus_id: filters.campus_id }),
                ...(eventCategory && { event_category: eventCategory }),
                ...(eventName && { event_name: eventName }),
                ...(userUuid && { user_uuid: userUuid }),
                ...(sessionId && { session_id: sessionId }),
                ...(routeFilter && { route: routeFilter }),
                ...(requestId && { request_id: requestId }),
                ...(featureAreaLocal && { feature_area: featureAreaLocal }),
                ...(deployVersionLocal && { deployment_version: deployVersionLocal }),
                ...(filters.feature_area && !featureAreaLocal && { feature_area: filters.feature_area }),
                ...(filters.deployment_version && !deployVersionLocal && { deployment_version: filters.deployment_version }),
            };
            const res = await adminAnalytics.getEvents(params);
            setEvents(res.data.events || []);
            setTotal(res.data.total || 0);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 401 || status === 403) {
                setError('__auth__');
            } else {
                setError(err.response?.data?.detail || 'Failed to load events.');
            }
        } finally {
            setLoading(false);
        }
    }, [filters.days, filters.district_id, filters.campus_id, filters.feature_area, filters.deployment_version,
        page, pageSize, eventCategory, eventName, userUuid, sessionId, routeFilter, requestId, featureAreaLocal, deployVersionLocal]);

    useEffect(() => { loadData(); }, [loadData]);
    useEffect(() => { setPage(1); }, [eventCategory, eventName, userUuid, sessionId, routeFilter, requestId, featureAreaLocal, deployVersionLocal, filters.days]);

    const totalPages = Math.ceil(total / pageSize);

    const handleViewTimeline = (uuid) => {
        setTimelineUser(uuid);
    };

    return (
        <div>
            <div style={{ marginBottom: 20 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>Event Explorer</h2>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
                    Debug user journeys with raw telemetry events · newest first
                </p>
            </div>

            {/* Filter row */}
            <div style={{
                display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap',
                alignItems: 'center',
            }}>
                <Filter size={14} style={{ color: '#94a3b8' }} />
                <FilterInput placeholder="Category" value={eventCategory} onChange={setEventCategory} width={100} />
                <FilterInput placeholder="Event name" value={eventName} onChange={setEventName} width={140} />
                <FilterInput placeholder="User UUID" value={userUuid} onChange={setUserUuid} width={120} />
                <FilterInput placeholder="Session ID" value={sessionId} onChange={setSessionId} width={120} />
                <FilterInput placeholder="Route" value={routeFilter} onChange={setRouteFilter} width={100} />
                <FilterInput placeholder="Request ID" value={requestId} onChange={setRequestId} width={120} />
                <FilterInput placeholder="Feature Area" value={featureAreaLocal} onChange={setFeatureAreaLocal} width={110} />
                <FilterInput placeholder="Deploy Ver." value={deployVersionLocal} onChange={setDeployVersionLocal} width={110} />
                <span style={{ fontSize: 11, color: '#94a3b8' }}>
                    {total.toLocaleString()} events
                </span>
            </div>

            {/* Auth error */}
            {error === '__auth__' && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '12px 16px', background: '#fffbeb', borderRadius: 10,
                    marginBottom: 16, border: '1px solid #fde68a',
                }}>
                    <ShieldAlert size={15} style={{ color: '#92400e' }} />
                    <div>
                        <span style={{ fontSize: 13, color: '#92400e', fontWeight: 500 }}>Session expired or not authenticated</span>
                        <p style={{ fontSize: 11, color: '#78716c', margin: '2px 0 0' }}>
                            Log in with an admin account to query events. <button onClick={loadData} style={{ fontSize: 11, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Retry</button>
                        </p>
                    </div>
                </div>
            )}

            {/* General error */}
            {error && error !== '__auth__' && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '10px 16px', background: '#fef2f2', borderRadius: 10,
                    marginBottom: 16, fontSize: 13, color: '#ef4444',
                }}>
                    <AlertTriangle size={14} /> {error}
                    <button onClick={loadData} style={{ fontSize: 11, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', marginLeft: 8 }}>Retry</button>
                </div>
            )}

            {/* Table */}
            <div style={{
                background: '#fff', border: '1px solid #e5e7eb',
                borderRadius: 12, overflow: 'hidden', marginBottom: 16,
            }}>
                {/* Header */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '130px 1fr 90px 100px 100px 40px',
                    padding: '10px 16px', background: '#f8fafc',
                    borderBottom: '1px solid #e5e7eb', gap: 8,
                }}>
                    {['Timestamp', 'Event', 'Category', 'User', 'Session', ''].map(h => (
                        <span key={h} style={{
                            fontSize: 11, fontWeight: 600, color: '#6b7280',
                            textTransform: 'uppercase',
                        }}>{h}</span>
                    ))}
                </div>

                {/* Rows */}
                {loading ? (
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: '40px 0', gap: 8,
                    }}>
                        <Loader2 size={18} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
                        <span style={{ fontSize: 13, color: '#6b7280' }}>Loading events…</span>
                    </div>
                ) : events.length === 0 ? (
                    <div style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        padding: '40px 0', gap: 8,
                    }}>
                        <DatabaseZap size={24} style={{ color: '#94a3b8' }} />
                        <span style={{ fontSize: 13, color: '#94a3b8' }}>No events matching these filters</span>
                    </div>
                ) : (
                    events.map((ev, i) => (
                        <div
                            key={ev.id || i}
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '130px 1fr 90px 100px 100px 40px',
                                padding: '10px 16px',
                                borderBottom: i < events.length - 1 ? '1px solid #f1f5f9' : 'none',
                                cursor: 'pointer', transition: 'background 0.1s', gap: 8,
                                fontSize: 12, color: '#374151',
                            }}
                            onClick={() => setSelectedEvent(ev)}
                            onMouseOver={e => e.currentTarget.style.background = '#f8fafc'}
                            onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                        >
                            <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#6b7280' }}>
                                {ev.created_at ? new Date(ev.created_at).toLocaleString('en-US', {
                                    month: 'short', day: 'numeric',
                                    hour: '2-digit', minute: '2-digit', second: '2-digit',
                                }) : '—'}
                            </span>
                            <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {ev.event_name || '—'}
                            </span>
                            <span>
                                <span style={{
                                    fontSize: 10, fontWeight: 600, padding: '2px 6px',
                                    borderRadius: 4, background: getCategoryColor(ev.event_category).bg,
                                    color: getCategoryColor(ev.event_category).text,
                                }}>
                                    {ev.event_category || '—'}
                                </span>
                            </span>
                            <span style={{
                                fontFamily: 'monospace', fontSize: 10, color: '#94a3b8',
                                overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>
                                {ev.user_uuid ? ev.user_uuid.slice(0, 8) + '…' : '—'}
                            </span>
                            <span style={{
                                fontFamily: 'monospace', fontSize: 10, color: '#94a3b8',
                                overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>
                                {ev.session_id ? ev.session_id.slice(0, 8) + '…' : '—'}
                            </span>
                            {ev.user_uuid && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleViewTimeline(ev.user_uuid); }}
                                    title="View user timeline"
                                    style={{
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        padding: 2, color: '#6366f1',
                                    }}
                                >
                                    <User size={13} />
                                </button>
                            )}
                        </div>
                    ))
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
                }}>
                    <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page <= 1}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 4,
                            padding: '6px 12px', borderRadius: 8,
                            border: '1px solid #e2e8f0', fontSize: 12,
                            background: '#fff', cursor: page <= 1 ? 'default' : 'pointer',
                            color: '#64748b', opacity: page <= 1 ? 0.4 : 1,
                        }}
                    >
                        <ChevronLeft size={14} /> Prev
                    </button>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>
                        Page {page} of {totalPages}
                    </span>
                    <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 4,
                            padding: '6px 12px', borderRadius: 8,
                            border: '1px solid #e2e8f0', fontSize: 12,
                            background: '#fff', cursor: page >= totalPages ? 'default' : 'pointer',
                            color: '#64748b', opacity: page >= totalPages ? 0.4 : 1,
                        }}
                    >
                        Next <ChevronRight size={14} />
                    </button>
                </div>
            )}

            {/* Payload drawer */}
            {selectedEvent && (
                <EventPayloadDrawer
                    event={selectedEvent}
                    onClose={() => setSelectedEvent(null)}
                />
            )}

            {/* User timeline drawer */}
            <UserTimelineDrawer
                userUuid={timelineUser}
                open={!!timelineUser}
                onClose={() => setTimelineUser(null)}
                days={filters.days || 7}
            />

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};


const FilterInput = ({ placeholder, value, onChange, width = 140 }) => (
    <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
            width, padding: '5px 10px', borderRadius: 6,
            border: '1px solid #e2e8f0', fontSize: 12,
            background: '#fff', color: '#374151',
            outline: 'none',
        }}
        onFocus={e => e.target.style.borderColor = '#6366f1'}
        onBlur={e => e.target.style.borderColor = '#e2e8f0'}
    />
);


const getCategoryColor = (category) => {
    const map = {
        acquisition: { bg: '#dbeafe', text: '#1d4ed8' },
        activation: { bg: '#fef3c7', text: '#92400e' },
        engagement: { bg: '#d1fae5', text: '#065f46' },
        outcome: { bg: '#ede9fe', text: '#5b21b6' },
        revenue: { bg: '#fce7f3', text: '#9d174d' },
        system: { bg: '#fef2f2', text: '#991b1b' },
        admin: { bg: '#f1f5f9', text: '#334155' },
    };
    return map[category] || { bg: '#f3f4f6', text: '#6b7280' };
};


export default EventExplorerTabV2;
